import { useState } from "react";
import { useSite } from "@/context/SiteContext";
import { api, apiErr } from "@/lib/api";
import { Section, GoldButton } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { toast } from "sonner";
import { Mail, MessageCircle, Youtube, Instagram, Send, CheckCircle2 } from "lucide-react";
import { getRecaptchaToken } from "@/lib/recaptcha";

const SUBJECTS = ["Consultation", "Content Plan", "Video Editing", "YouTube Setup", "Community", "Affiliate/Collab", "Other"];

export default function Contact() {
  const { settings } = useSite();
  const [f, setF] = useState({ name: "", email: "", whatsapp: "", subject: "", message: "" });
  const [hp, setHp] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken(settings.recaptcha_site_key, "contact");
      await api.post("/contact", { ...f, hp, recaptcha_token });
      setDone(true);
      toast.success("Message sent! We'll reply by email soon.");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not send. Try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";

  return (
    <div className="relative grain overflow-hidden">
      <Section>
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Let's talk.</h1>
            <p className="mt-5 text-white/70 max-w-md text-lg">Have a question or not sure where to start? Send a message — I read every one.</p>
            <div className="mt-8 space-y-4">
              {settings.whatsapp_link && (
                <a href={settings.whatsapp_link} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
                  className="flex items-center gap-3 rounded-xl border border-white/10 p-4 hover:border-[#25D366]/60 transition-colors">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-[#25D366]/15 text-[#25D366]"><MessageCircle size={20} /></span>
                  <div><div className="text-sm text-white/50">WhatsApp</div><div className="text-white">{settings.whatsapp_number}</div></div>
                </a>
              )}
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 rounded-xl border border-white/10 p-4 hover:border-[#D4AF37]/60 transition-colors">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]"><Mail size={20} /></span>
                  <div><div className="text-sm text-white/50">Email</div><div className="text-white">{settings.contact_email}</div></div>
                </a>
              )}
              <div className="flex gap-3 pt-2">
                {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="grid place-items-center h-11 w-11 rounded-full border border-white/15 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"><Youtube size={18} /></a>}
                {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="grid place-items-center h-11 w-11 rounded-full border border-white/15 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"><Instagram size={18} /></a>}
                {settings.telegram_link_enabled && settings.telegram_link && <a href={settings.telegram_link} target="_blank" rel="noreferrer" data-testid="contact-social-telegram" className="grid place-items-center h-11 w-11 rounded-full border border-white/15 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"><Send size={18} /></a>}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="contact-success" className="card-surface rounded-2xl p-8 text-center gold-glow">
                <CheckCircle2 className="mx-auto text-[#D4AF37]" size={44} />
                <h3 className="mt-4 text-xl font-display">Message received!</h3>
                <p className="mt-2 text-white/70 text-sm">Thanks for reaching out. We've sent a confirmation to your email and will reply soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="card-surface rounded-2xl p-6 sm:p-8 space-y-4">
                <input type="text" tabIndex={-1} value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Name *</label>
                    <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" className={inputCls} /></div>
                  <div><label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Email *</label>
                    <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" className={inputCls} /></div>
                  <div><label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">WhatsApp</label>
                    <input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="contact-whatsapp-input" className={inputCls} /></div>
                  <div><label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Subject *</label>
                    <select required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject" className={inputCls}>
                      <option value="">Select...</option>{SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                </div>
                <div><label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Message *</label>
                  <textarea required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" className={inputCls} /></div>
                <GoldButton type="submit" disabled={loading} data-testid="contact-submit" className="w-full sm:w-auto">{loading ? "Sending..." : "Send Message"}</GoldButton>
              </form>
            )}
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
